import {
  arrIncludesArrayItem,
  Filter,
  numberInRange,
  PageSizeSelect,
  PaginationCounter,
  Paginator,
  resolveTableUpdater,
  TableContext,
  ToggleButton,
  usePersistentTableState,
} from '@eye-able/ui';
import type { TableContextType, TableState } from '@eye-able/ui';
import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  AccessorKeyColumnDef,
  ColumnDef,
  PaginationState,
  RowData,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import {
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import TableItemCard from '../Card/TableItemCard';
import { LayoutGrid } from 'lucide-react';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export type CardListView = 'list' | 'grid';

export type CardListRowRendererProps<TData extends RowData> = {
  row: TData;
  view: CardListView;
};

export type CardListRowTransformContext = {
  sorting: SortingState;
  view: CardListView;
};

export type CardListProps<TData extends RowData> = {
  actions?: ReactNode;
  data?: TData[];
  columns?: ColumnDef<TData, unknown>[];
  id?: string;
  hideFilter?: boolean;
  searchColumnId?: string[];
  defaultSortBy?: string;
  defaultSortDirection?: 'asc' | 'desc';
  defaultView?: CardListView;
  disableViewToggle?: boolean;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData, index: number) => string | number;
  rowTitle: ComponentType<CardListRowRendererProps<TData>>;
  rowMain: ComponentType<CardListRowRendererProps<TData>>;
  rowTitleAction?: ComponentType<CardListRowRendererProps<TData>>;
  transformRows?: (
    rows: TData[],
    context: CardListRowTransformContext,
  ) => TData[];
  pageSize?: number;
  rowsPerPageOptions?: number[];
  hidePagination?: boolean;
};

function getColumnId<TData extends RowData>(
  column: ColumnDef<TData, unknown> | undefined,
): string | undefined {
  if (!column) return undefined;
  const accessorColumn = column as AccessorKeyColumnDef<TData, unknown>;
  return (
    column.id ??
    (typeof accessorColumn.accessorKey === 'string'
      ? accessorColumn.accessorKey
      : undefined)
  );
}

export default function CardList<TData extends RowData>({
  actions,
  data = [],
  columns = [],
  id = 'card-list',
  hideFilter = false,
  searchColumnId,
  defaultSortBy,
  defaultSortDirection = 'asc',
  defaultView = 'list',
  hidePagination = false,
  disableViewToggle = false,
  onRowClick,
  pageSize = 50,
  rowsPerPageOptions = [10, 20, 50],
  getRowId = (row, index) => {
    const candidate = (row as { id?: string | number }).id;
    return candidate ?? index;
  },
  rowTitle: RowTitle,
  rowMain: RowMain,
  rowTitleAction: RowTitleAction,
  transformRows,
}: CardListProps<TData>) {
  const { t } = useInlineTranslation();

  const searchKeys = useMemo(() => {
    if (searchColumnId) return searchColumnId;
    const fallback = getColumnId(columns[0]);
    return fallback ? [fallback] : [];
  }, [columns, searchColumnId]);

  const initialSortColumnId = useMemo(() => {
    const firstSortableColumn = columns.find(
      (column) => column.enableSorting !== false && getColumnId(column),
    );
    return defaultSortBy ?? getColumnId(firstSortableColumn);
  }, [columns, defaultSortBy]);

  const tableId = useMemo(() => {
    const columnsKey = columns
      .map(
        (column) =>
          (column as AccessorKeyColumnDef<TData, unknown>)?.accessorKey,
      )
      .sort()
      .join('_');

    return `${window.location.pathname}_${id}_${columnsKey}`;
  }, [columns, id]);

  const viewStorageKey = `${tableId}_view`;

  const defaultTableState = useMemo<TableState>(() => {
    return {
      sorting: initialSortColumnId
        ? [
            {
              id: initialSortColumnId,
              desc: defaultSortDirection === 'desc',
            },
          ]
        : [],
      columnFilters: [],
      columnVisibility: {},
    };
  }, [defaultSortDirection, initialSortColumnId]);

  const [view, setView] = useState<CardListView>(() => {
    const storedView = localStorage.getItem(viewStorageKey);

    return storedView === 'grid' || storedView === 'list'
      ? storedView
      : defaultView;
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [tableState, setTableState] = usePersistentTableState(
    tableId,
    defaultTableState,
  );

  useEffect(() => {
    localStorage.setItem(viewStorageKey, view);
  }, [viewStorageKey, view]);

  const handleSortingChange = (updater: Updater<SortingState>) => {
    setTableState((prev) => {
      const next = resolveTableUpdater(updater, prev.sorting ?? []);
      if (!next.length) {
        return {
          ...prev,
          sorting: next,
        };
      }

      const nextPrimary = next[0];
      const prevPrimary = prev.sorting?.[0];
      const isNewColumn = prevPrimary?.id !== nextPrimary.id;

      let resolvedSorting = next;

      if (isNewColumn && !nextPrimary.desc) {
        const column = columns.find(
          (col) => getColumnId(col) === nextPrimary.id,
        );
        if (column?.sortDescFirst) {
          resolvedSorting = [{ ...nextPrimary, desc: true }, ...next.slice(1)];
        }
      }

      return {
        ...prev,
        sorting: resolvedSorting,
      };
    });
  };
  const table = useReactTable<TData>({
    data,
    columns,
    state: {
      globalFilter,
      columnFilters: tableState.columnFilters,
      columnVisibility: tableState.columnVisibility,
      sorting: tableState.sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnFilters: resolveTableUpdater(updater, prev.columnFilters ?? []),
      })),
    onColumnVisibilityChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnVisibility: resolveTableUpdater(
          updater,
          prev.columnVisibility ?? {},
        ),
      })),
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const query = String(filterValue ?? '')
        .trim()
        .toLowerCase();
      if (!query) return true;

      return searchKeys.some((key) =>
        String(row.getValue(key) ?? '')
          .toLowerCase()
          .includes(query),
      );
    },
    filterFns: {
      arrIncludesArrayItem: (row, columnId, filterValues, addMeta) => {
        if (arrIncludesArrayItem(row, columnId, filterValues, addMeta)) {
          return true;
        }

        if (!Array.isArray(filterValues) || filterValues.length === 0) {
          return false;
        }

        const cellValue = row.getValue(columnId);
        if (Array.isArray(cellValue)) {
          return cellValue.some((value) => filterValues.includes(value));
        }

        if (typeof cellValue === 'string') {
          const normalizedCellValues = cellValue
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          return normalizedCellValues.some((value) =>
            filterValues.includes(value),
          );
        }

        return false;
      },
      numberInRange,
    },
  });

  const filteredAndSortedRows = table.getSortedRowModel().rows;

  const renderedRows = useMemo(() => {
    const rows = filteredAndSortedRows.map((tableRow) => tableRow.original);

    if (!transformRows) {
      return rows;
    }

    return transformRows(rows, {
      sorting: tableState.sorting,
      view,
    });
  }, [filteredAndSortedRows, tableState.sorting, transformRows, view]);

  const paginatedRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return renderedRows.slice(start, start + pagination.pageSize);
  }, [pagination.pageIndex, pagination.pageSize, renderedRows]);

  useEffect(() => {
    const maxPageIndex = Math.max(
      Math.ceil(renderedRows.length / pagination.pageSize) - 1,
      0,
    );

    if (pagination.pageIndex > maxPageIndex) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: maxPageIndex,
      }));
    }
  }, [pagination.pageIndex, pagination.pageSize, renderedRows.length]);

  const contextValue = useMemo<Partial<TableContextType<TData>>>(() => {
    return {
      globalFilter,
      setGlobalFilter,
      table,
      tableState,
    };
  }, [globalFilter, table, tableState]);

  return (
    <TableContext.Provider
      value={contextValue as unknown as TableContextType<unknown>}
    >
      <div className='eye:flex eye:w-full eye:flex-col'>
        {!hideFilter && (
          <Filter
            disableFullscreen
            disableHideShowColumns
            actions={
              !disableViewToggle ? (
                <div className='eye:flex eye:items-center eye:gap-4xs eye:mr-2xs'>
                  {actions}
                  <ToggleButton
                    pressed={view === 'grid'}
                    aria-label={view === 'grid' ? t('listView') : t('gridView')}
                    className='eye:bg-white'
                    onPressedChange={(pressed) => {
                      setView(pressed ? 'grid' : 'list');
                    }}
                  >
                    <LayoutGrid className='eye:stroke-[1.5]' aria-hidden />
                  </ToggleButton>
                </div>
              ) : (
                actions
              )
            }
          />
        )}

        {renderedRows.length === 0 ? (
          <div className='eye:body-m eye:py-16 eye:text-center eye:text-grey-100'>
            <T k='noResultsFound' />
          </div>
        ) : (
          <>
            <div
              className={
                view === 'grid'
                  ? 'eye:grid eye:grid-cols-1 eye:gap-2xs eye:md:grid-cols-2 eye:xl:grid-cols-3'
                  : 'eye:flex eye:flex-col eye:gap-2xs'
              }
            >
              {paginatedRows.map((row, index) => {
                const rowId = getRowId(
                  row,
                  pagination.pageIndex * pagination.pageSize + index,
                );
                const rowRendererProps = { row, view };

                return (
                  <TableItemCard
                    key={rowId}
                    title={<RowTitle {...rowRendererProps} />}
                    titleAction={
                      RowTitleAction ? (
                        <RowTitleAction {...rowRendererProps} />
                      ) : null
                    }
                    main={<RowMain {...rowRendererProps} />}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  />
                );
              })}
            </div>

            {!hidePagination && renderedRows.length > 1 && (
              <div className='eye:my-8 eye:grid eye:grid-cols-3 eye:items-center eye:gap-y-12'>
                <PaginationCounter
                  className='eye:hidden eye:md:block'
                  page={pagination.pageIndex + 1}
                  pageSize={pagination.pageSize}
                  totalItems={renderedRows.length}
                />
                <Paginator
                  page={pagination.pageIndex + 1}
                  pageCount={Math.ceil(
                    renderedRows.length / pagination.pageSize,
                  )}
                  onPageChange={(page) =>
                    setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))
                  }
                />
                <PageSizeSelect
                  className='eye:justify-end'
                  value={pagination.pageSize}
                  options={rowsPerPageOptions}
                  onValueChange={(size) =>
                    setPagination((prev) => ({ ...prev, pageSize: size }))
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </TableContext.Provider>
  );
}
